import { Controller } from "@hotwired/stimulus"

// Shared send-for-signatures modal (signature_chains/_builder_modal):
// ordered signer rows (project member picker or free email) posted to the
// document's start_signature_chain endpoint.
export default class extends Controller {
  // signName only exists in self-sign mode (CO approve-and-sign): the opener
  // signs in-app immediately, so extra signer rows are optional. certAgree
  // only exists on pay applications: the Contractor's Certification checkbox
  // that must be checked before Approve & Sign.
  static targets = ["modal", "rows", "rowTemplate", "signName", "certAgree", "certWrap"]
  static values = { url: String, savedSigners: Array, defaultRole: String, blockedMessage: String }

  open() {
    // Sequential Signing: an out-of-turn document explains instead of opening.
    if (this.blockedMessageValue) return this._showToast(this.blockedMessageValue, "alert")
    if (!this.rowsTarget.children.length) {
      // Pre-fill the last signer list used on this project (still editable).
      let saved = []
      try { saved = this.savedSignersValue || [] } catch (e) { saved = [] }
      if (saved.length) {
        saved.forEach((s) => this._addPrefilledRow(s))
      } else if (!this.hasSignNameTarget) {
        this.addRow()
      } else if (this.defaultRoleValue) {
        // Self-sign mode with a default recipient role (PRs): start with one
        // row, Role pre-set (e.g. Owner) — still fully editable/removable.
        this.addRow()
        this.rowsTarget.lastElementChild.querySelector("[data-chain-role]").value = this.defaultRoleValue
      }
    }
    this.modalTarget.classList.remove("hidden")
  }

  _addPrefilledRow(signer) {
    this.addRow()
    const row = this.rowsTarget.lastElementChild
    const member = row.querySelector("[data-chain-member]")
    const option = signer.user_id && member.querySelector(`option[value="${signer.user_id}"]`)
    if (option) {
      member.value = String(signer.user_id)
      member.dispatchEvent(new Event("change")) // fills + locks name/email
    } else {
      row.querySelector("[data-chain-name]").value = signer.name || ""
      row.querySelector("[data-chain-email]").value = signer.email || ""
    }
    if (signer.role_label) row.querySelector("[data-chain-role]").value = signer.role_label
  }

  close() { this.modalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  addRow() {
    this.rowsTarget.appendChild(this.rowTemplateTarget.content.cloneNode(true))
    const row = this.rowsTarget.lastElementChild
    row.querySelector("[data-chain-member]").addEventListener("change", (e) => {
      const opt = e.target.selectedOptions[0]
      const name = row.querySelector("[data-chain-name]")
      const email = row.querySelector("[data-chain-email]")
      if (opt.value) {
        name.value = opt.dataset.name || ""
        email.value = opt.dataset.email || ""
        name.readOnly = email.readOnly = true
      } else {
        name.readOnly = email.readOnly = false
      }
    })
    this._renumber()
  }

  removeRow(e) {
    e.currentTarget.closest("[data-chain-row]").remove()
    this._renumber()
  }

  async start() {
    const signers = Array.from(this.rowsTarget.querySelectorAll("[data-chain-row]"))
      .map((row) => ({
        user_id: row.querySelector("[data-chain-member]").value || null,
        name: row.querySelector("[data-chain-name]").value,
        email: row.querySelector("[data-chain-email]").value,
        role_label: row.querySelector("[data-chain-role]").value
      }))
      .filter((s) => s.email || s.name) // ignore rows left fully empty
    if (signers.some((s) => !s.email)) {
      return this._showToast("Every signer needs an email address.", "alert")
    }

    // The Contractor's Certification must be agreed to before signing (PRs).
    if (this.hasCertAgreeTarget && !this.certAgreeTarget.checked) {
      if (this.hasCertWrapTarget) {
        this.certWrapTarget.classList.add("text-red-600")
        this.certAgreeTarget.classList.add("ring-2", "ring-red-400")
        this.certAgreeTarget.addEventListener("change", () => {
          this.certWrapTarget.classList.remove("text-red-600")
          this.certAgreeTarget.classList.remove("ring-2", "ring-red-400")
        }, { once: true })
      }
      return this._showToast("Please check the box to agree to the Contractor's Certification before signing.", "alert")
    }

    const body = { signers }
    let flash = "Sent — the first signer just got their signing link"
    if (this.hasSignNameTarget) {
      body.signed_name = this.signNameTarget.value.trim()
      if (!body.signed_name) return this._showToast("Type your name to sign.", "alert")
      flash = signers.length
        ? "Signed — the next signer just got their signing link"
        : "Signed & approved — the contract sum is updated"
    } else if (!signers.length) {
      return this._showToast("Every signer needs an email address.", "alert")
    }

    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(body)
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not start the signature chain")
      sessionStorage.setItem("builder-finance.flash", flash)
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  _renumber() {
    this.rowsTarget.querySelectorAll("[data-chain-pos]").forEach((el, i) => { el.textContent = i + 1 })
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
